<template>
    <div class="game">
        <div v-if="!gameStarted" class="overlay">
            <div class="content overlayContent">
                <div class="row">
                    <div class="col">
                        <img :src="`/images/${celebrityInvite}pic.png`" :title="friendName" :alt="friendName" />
                    </div>
                </div>
                <div class="row">
                    <div class="col p-5">
                        <h1>You are playing with {{ friendName }}!</h1>
                        <h1 class="mt-2">See how many points can you get in 30 seconds.</h1>
                    </div>
                </div>
                <div class="row">
                    <div class="col">
                        <b-button @click="initGame" class="startButton btn-start">GO!</b-button>
                    </div>
                </div>
            </div>
        </div>
        <div class="content">
            <div class="container game__container text-center">
                <div class="row">
                    <div class="col text-left">
                        <h3>00:{{ formatTime(gameLength) }}</h3>
                    </div>
                    <div class="col text-right pr-5">
                        <h3>{{ points }}</h3>
                    </div>
                </div>
                <div class="row progress-wrapper text-left">
                    <div class="col">
                        <h3>{{ friendName }}</h3>
                        <ScoreProgressBar progressBarType="wrong"
                                          :score="friendScore === 0 ? 1 : friendScore + 1"
                                          :totalScore="totalScore"
                                          :showText="false"
                        />
                    </div>
                </div>
                <div class="mt-sm-2 row progress-wrapper text-left">
                    <div class="col">
                        <h3>You</h3>
                        <ScoreProgressBar progressBarType="correct"
                                          :score="points === 0 ? 1 : points + 1"
                                          :totalScore="totalScore"
                                          :showText="false"
                        />
                    </div>
                </div>
                <div class="row">
                    <div v-if="countDownCounter > 0 && gameStarted" class="col question-wrapper start-counter">
                        <h1 class="start-counter">{{ countDownWords[countDownCounter] }}</h1>
                    </div>
                    <div v-else-if="gameStarted" class="col question-wrapper question">
                        <h1>{{ question.data.number1 }} {{ question.icon }} {{ question.data.number2 }} = </h1>
                    </div>
                    <div v-else class="col question-wrapper question">
                        <h1>1 + 2 = </h1>
                    </div>
                </div>
                <div v-if="gameLength > 0" class="row counter">
                    <div class="col answer-wrapper">
                        <span class="c-input">
                            <input type="tel"
                                   pattern="[0-9]*"
                                   ref="answerInput"
                                   v-model="answer"
                                   :readonly="isReadOnly"
                                   @keyup.enter="playerAnswered"
                            />
                        </span>
                    </div>
                </div>
            </div>
        </div>
        <div class="keyboard-wrapper">
            <OnScreenKeyboard
                    :handleKeyboardClick="handleKeyboardClick"
                    :handleKeyboardDelete="handleKeyboardDelete"
                    :answerQuestion="playerAnswered"
            />
        </div>
    </div>
</template>

<script>
    import Vue from 'vue'
    import OnScreenKeyboard from "../components/OnScreenKeyboard";
    import ScoreProgressBar from "../components/ScoreProgressBar";
    import { GameTypes } from "../services/GameTypes";
    import { celebrities } from "../services/CelebrityScores";

    const gameTimeInSeconds = 30;
    const gameTypeFlow = [
        'addition1to20',
        'subtraction1to20',
        'subtraction20to100',
        'multiply1to100',
        'addition1to20','addition1to20',
        'addition20to100', 'addition20to100',
        'multiply1to100', // The last key will repeat until forever.
    ];
    const gameFlowLength = gameTypeFlow.length;

    export default {
        components: {
            ScoreProgressBar,
            OnScreenKeyboard,
        },
        props: {
            invite: {
                type: String,
                required: false,
            }
        },
        data() {
            return {
                isReadOnly: window.innerWidth < 768 || 'ontouchstart' in document.documentElement,
                countDownCounter: 2,
                countDownWords: ['', 'Go!', 'Ready?'],
                gameLength: gameTimeInSeconds,
                shareLink: '',
                invitePoints: null,
                gameStarted: false,
                question: {
                    type: null,
                    icon: null,
                    data: null,
                },
                answer: '',
                points: 0,
                totalAnswerCount: 0,
                correctAnswers: 0,
                wrongAnswers: 0,
                gameEnded: false,
                bot: false,
                friendScore: 0,
                friendName: 'Your friend',
                celebrityInvite: null,
                gameFlowIndex: 0,
            }
        },
        computed: {
            progress() {
                return parseInt(Math.floor((this.gameLength * 100) / gameTimeInSeconds));
            },
            totalScore() {
                if (this.invitePoints) {
                    if (this.invitePoints > this.points) {
                        return this.invitePoints + 1;
                    }
                    return this.points + 5;
                }
                return 50;
            }
        },
        mounted() {
            this.decodeInvite();
            this.generateQuestion();
        },
        methods: {
            initGame() {
                if (this.gameStarted === false) {
                    this.$ga.event({
                        eventCategory: 'game',
                        eventAction: 'click',
                        eventLabel: 'goBtn',
                    });
                    this.gameStarted = true;
                    this.countDown();
                }
            },
            decodeInvite() {
                let player = null;

                if (this.invite) {
                    player = celebrities.find(obj => {
                        return obj.invite === this.invite;
                    });
                    // If there's an invite key, but we didn't find a celebrity, then redirect home.
                    if (!player) {
                        window.location = '/';
                    }
                }

                if (player) {
                    this.invitePoints = player.points;
                    this.friendName = player.name;
                    this.celebrityInvite = this.invite;
                    this.$ga.event({
                        eventCategory: 'social',
                        eventAction: 'load',
                        eventLabel: 'loadedCelebrity',
                    });
                    this.$ga.event({
                        eventCategory: 'social',
                        eventAction: 'load',
                        eventLabel: `loadedCelebrity${player.name}With${player.points}`,
                    });
                } else {
                    this.invitePoints = celebrities[0].points;
                    this.friendName = celebrities[0].name;
                    this.celebrityInvite = celebrities[0].invite;
                }
            },
            countDown() {
                const countDownInterval = setInterval(() => {
                    this.countDownCounter--;
                    if (this.countDownCounter === 0) {
                        this.startGame();
                        clearInterval(countDownInterval);
                    }
                }, 800);
            },
            startGame() {
                this.focusOnInput();
                let second = 0;
                const gameLengthInterval = setInterval(() => {
                    this.gameLength -= 0.1;
                    if (this.gameLength < 0) {
                        clearInterval(gameLengthInterval);
                        this.gameOver();
                    } else {
                        if (this.invitePoints) {
                            second += 0.1;
                            const increaseFriendScore = (gameTimeInSeconds / this.invitePoints).toFixed(1);
                            if (second.toFixed(1) === increaseFriendScore) {
                                this.friendScore++;
                                second = 0;
                            }
                        }
                    }
                }, 100);
            },
            gameOver() {
                this.gameEnded = true;

                this.$router.push({
                    name: 'end-game', params: {
                        points: this.points,
                        totalAnswers: this.totalAnswerCount,
                        correctAnswers: this.correctAnswers,
                        wrongAnswers: this.wrongAnswers,
                        bot: this.bot,
                        invitePoints: this.invitePoints,
                        friendName: this.friendName,
                        celebrityInvite: this.celebrityInvite
                    }
                });
            },
            generateQuestion() {

                const gameFlowIndex = this.gameFlowIndex < gameFlowLength ? this.gameFlowIndex : gameFlowLength - 1;

                const {type, icon, numberGenerator} = GameTypes[gameTypeFlow[gameFlowIndex]];

                const generatedQuestion = Vue.$generateQuestion(type, numberGenerator);

                this.question = {
                    type,
                    icon,
                    data: generatedQuestion,
                };
                if (this.countDownCounter === 0) {
                    this.focusOnInput();
                }
                this.gameFlowIndex++;
            },
            playerAnswered() {
                this.totalAnswerCount++;
                if (parseInt(this.answer) === this.question.data.answer) {
                    this.points++;
                    this.correctAnswers++;
                } else {
                    this.points--;
                    this.wrongAnswers++;
                }
                this.answer = '';
                this.generateQuestion();
                this.focusOnInput();
            },
            async focusOnInput() {
                if (!this.isReadOnly) {
                    await this.$nextTick();
                    const input = this.$refs.answerInput;
                    if (input) {
                        input.focus();
                    }
                }
            },
            handleKeyboardClick(value) {
                this.answer += value.toString();
            },
            handleKeyboardDelete() {
                this.answer = this.answer.substr(0, this.answer.length - 1);
            },
            formatTime(time) {
                let seconds = Math.floor(time) % 60;
                let milliseconds = Math.floor(time * 100) % 100;
                if (seconds <= 9) {
                    seconds = `0${seconds}`;
                }
                if (milliseconds <= 9) {
                    milliseconds = `0${milliseconds}`;
                }
                return `${seconds}:${milliseconds}`;
            }
        },
    }
</script>



<style scoped lang="scss">
    .game__container {
        margin-top: 2rem;
    }
    .c-input {
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #fff;
        padding: 0;
        border: 1px solid transparent;
        border-radius: 20px;
        margin: 0 auto;
        box-shadow: 0 4px 4px rgba(0,0,0,0.2);
        max-width: 500px;
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }
        input {
            font-size: 2rem;
            font-weight: 500;
            border: none;
            background-color: transparent;
            width: 80%;
            text-align: center;
            &:focus {
                border: none;
                outline: none;
            }
        }
    }
    .keyboard-wrapper {
        width: 100vw;
        display: flex;
        justify-content: center;
        position: absolute;
        bottom: 0;
        margin-bottom: 40px;
        z-index: 5;
    }
    @media (max-width: 380px) {
        header nav {
            height: 40px;
        }
        .game__container {
            margin-top: 1rem;
        }
        .question-wrapper {
            h1 {
                font-size: 28px;
            }
        }
        .answer-wrapper {
            input {
                font-size: 1.5rem;
            }
        }
    }
    @media (max-width: 767px) {
        .keyboard-wrapper {
            margin-bottom: 1px;
        }
    }
    .start-counter h1 {
        animation: right_to_left 0.8s;
        animation-iteration-count: infinite;
    }

    .question h1 {
        animation: right_to_center 1s;
    }

    @keyframes right_to_left {
        from {
            margin-left: 35%;
        }
        to {
            margin-left: -35%;
        }
    }
    @keyframes right_to_center {
        from {
            margin-left: 35%;
        }
        to {
            margin-left: 0;
        }
    }

    .overlay {
        position: fixed;
        display: block;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0,0,0,0.7);
        z-index: 10;
    }
    .overlayContent {
        display: flex;
        flex-direction: column;
        text-align: center;
        justify-content: center;
        height: 100%;
        h1 {
            font-size: 1.7rem;
        }
        img {
            max-height: 200px;
            width: 95%;
            max-width: 200px;
            margin: 10px;
        }
        @media (min-width: 767px) {
            h1 {
                font-size: 2.5rem;
            }
            img {
                max-height: 350px;
                max-width: 350px;
            }
        }
    }
    .startButton {
        margin-top: 20px;
    }
    .startText {
        font-size: 40px;
        margin-right: 20px;
        margin-left: 20px;
    }
    @media (max-width: 380px) {
        h3 {
            font-size: initial;
            margin: 0;
        }
    }
</style>
